import { useState } from 'react'
import { Navigate, Outlet, useLocation } from 'react-router-dom'
import styled from 'styled-components'
import { useInactivityTimeout } from 'hooks/useInactivityTimeout'
import { Header, PlaceHolder } from 'components'
import { USER_ROLES } from 'config/roles'
import { UserProvider } from 'providers/UserProvider'
import { useSelector } from 'react-redux'
import { useAuthToken, useLockedCardStatusUpdater } from 'hooks'

const ProtectedRoutesOutlet = () => {
  const location = useLocation()
  const { isAuthenticated, loading: authLoading } = useAuthToken()
  const userData = useSelector(state => state?.auth?.data)
  const checkAndUpdateStatusForLockedCards = useLockedCardStatusUpdater()
  useInactivityTimeout(checkAndUpdateStatusForLockedCards)
  const [background, setBackground] = useState('#fafafa')
  const currentUser = userData?.current_user
  const isAgent = currentUser?.affiliate_role === USER_ROLES.AGENT
  const is2FAEnabled = currentUser?.google_authentication_enabled === '1'
  const is2FAVerified = currentUser?.google_authentication_code_verified === '1'
  const mustVerify2FA = Boolean(isAgent && is2FAEnabled && !is2FAVerified)

  if (authLoading) {
    return <PlaceHolder />
  }

  if (!isAuthenticated) {
    const currentPath = location.pathname + location.search
    const shouldPreservePath =
      currentPath &&
      currentPath !== '/' &&
      currentPath !== '/login' &&
      currentPath !== '/forgot-password' &&
      !currentPath.startsWith('/public/')

    if (shouldPreservePath) {
      const encodedPath = encodeURIComponent(currentPath)
      return <Navigate to={`/login?redirect=${encodedPath}`} replace />
    }
    return <Navigate to="/login" replace />
  }

  return (
    <AppWrapper>
      <Header
        disableNavigation={mustVerify2FA}
        verificationBanner={
          mustVerify2FA
            ? 'Set up google authenticator in Profile to access portal features'
            : ''
        }
      />
      <LayoutContainer $bg={background}>
        {mustVerify2FA && location.pathname !== '/profile' && (
          <Navigate to="/profile" replace />
        )}
        <ContentWrapper>
          <UserProvider>
            <Outlet context={{ setBackground }} />
          </UserProvider>
        </ContentWrapper>
      </LayoutContainer>
    </AppWrapper>
  )
}

const AppWrapper = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
`

const LayoutContainer = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  width: 100%;
  overflow: hidden;
  background: ${({ $bg }) => $bg};
`

const ContentWrapper = styled.main`
  flex: 1;
  overflow-y: auto;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
`

export default ProtectedRoutesOutlet
